import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';

import {
  dummy as melodyEditorDummy,
  show as showMelodyEditor,
} from '../MelodyEditor/melodiesSlice';

import bluejay from './images/bluejay_logo.png';
import './style.scss';

function Install() {
  const { t } = useTranslation('common');

  const deferredPrompt = useRef(null);
  const [showInstall, setShowInstall]  = useState(false);

  window.addEventListener('beforeinstallprompt', (e) => {
    deferredPrompt.current = e;
    setShowInstall(true);
  });

  const handleInstallToHomescreen = useCallback(() => {
    if(deferredPrompt.current) {
      deferredPrompt.current.prompt();
    }
  }, [deferredPrompt]);

  return(
    <div className={`install-wrapper ${showInstall ? 'active' : ''}`}>
      <div className="install">
        <div className="description">
          <ReactMarkdown>
            {t('homeInstallLine1')}
          </ReactMarkdown>

          <ReactMarkdown>
            {t('homeInstallLine2')}
          </ReactMarkdown>
        </div>

        <div className="default-btn">
          <button
            onClick={handleInstallToHomescreen}
            type="button"
          >
            {t('addToHomeScreen')}
          </button>
        </div>
      </div>
    </div>
  );
}

function WelcomeDialog({ onConfirm }) {
  const { t } = useTranslation('common');
  const [agreed, setAgreed] = useState(false);

  const handleConfirm = useCallback(() => {
    if (agreed) {
      localStorage.setItem('esc_configurator_welcome_shown', 'true');
      onConfirm();
    }
  }, [agreed, onConfirm]);

  const handleCheckboxChange = useCallback(() => {
    setAgreed((prev) => !prev);
  }, []);

  return (
    <div className="welcome-dialog-overlay">
      <div className="welcome-dialog">
        <div className="welcome-header">
          <svg
            fill="#4a90e2"
            height="60"
            viewBox="0 0 512 512"
            width="60"
          >
            <path
              d="M237.6 14.8c47.2-5 96.3 10.3 131.9 41.8 5.3 4.9 12.5 9.4 13.4 17.2 1.3 8.8-6.8 17.5-15.8 16.4-8-.4-12.3-8.1-18.1-12.4-21-17.9-47.2-29.6-74.6-33.1-42.8-6-88.1 8.3-119.2 38.5-3.5 3.7-7.8 7.3-13.2 7.1-7.4.4-14.2-5.8-14.9-13.2-.4-4.3 1.1-8.8 4.1-11.9 28.2-28.4 66.5-46.7 106.4-50.4zm0 49c39-5.7 80.5 8.2 107.6 36.9 4.9 5.7 4.3 15.2-1.4 20.1-4.7 4.6-12.7 5.3-18.1 1.5-2.9-2.1-5.3-4.7-8-7-14.9-12.9-34-21-53.6-22.7-26.2-2.7-53.4 6.1-73 23.6-3.6 3.1-6.8 7.2-11.7 8.2-7.7 2.1-16.4-3.6-17.8-11.4-1-4.7.5-9.9 4-13.3 19.3-19.5 45-32.3 72-35.9zm8 48c23.8-3.2 48.8 5.7 65.2 23.1 4.9 5.5 4.5 14.9-1 19.9-5.1 5.5-14.7 5.7-20.1.5-6.3-6.2-14-11-22.6-13.1-16-4.2-33.9.5-45.8 12.1-3.7 4.1-9.7 5.8-15 4.2-4.7-1.4-8.6-5.3-10-9.9-1.3-4.9-.3-10.4 3.3-14.1 12.1-12.6 28.6-20.8 46-22.7zm-2.7 61c6.3-4.1 14.6-4.9 21.6-2.3 8.6 3 15.1 11.2 16.1 20.3 1 7.8-2.1 15.7-7.7 21.1l4.3 28.1h-16.7l-3.3-21.5c-.6 0-1.7.1-2.3.1l-3.3 21.4h-16.7l4.3-28.1c-4.5-4.4-7.6-10.4-7.9-16.8-.6-8.9 4.1-17.7 11.6-22.3m9.4 13.6c-5.6 2.5-6.1 11.1-.9 14.2 5 3.7 13-.5 12.8-6.7.4-5.9-6.7-10.4-11.9-7.5z"
            />
          </svg>

          <h2>
            欢迎使用流星雨加速站
          </h2>

          <p className="subtitle">
            ESC Configurator 镜像服务
          </p>
        </div>

        <div className="welcome-sections">
          <div className="welcome-section">
            <h3>
              🚀 加速服务
            </h3>

            <p>
              本站为流星雨个人搭建的 ESC Configurator 国内加速镜像站，提供固件下载加速、API 代理加速等服务，解决国内访问 GitHub 速度慢、不稳定的问题。
            </p>
          </div>

          <div className="welcome-section">
            <h3>
              ⚠️ 风险提示
            </h3>

            <p>
              使用本站刷写的固件可能存在风险，请自行承担使用后果。对于因使用本站资源而导致的任何直接或间接损失，本站不承担任何责任。
            </p>
          </div>

          <div className="welcome-section">
            <h3>
              🌐 官方渠道
            </h3>

            <p>
              BLHeli GitHub：
              {' '}

              <a
                href="https://github.com/bitdump/BLHeli"
                rel="noopener noreferrer"
                target="_blank"
              >
                github.com/bitdump/BLHeli
              </a>

              <br />

              Bluejay GitHub：
              {' '}

              <a
                href="https://github.com/bird-sanctuary/bluejay"
                rel="noopener noreferrer"
                target="_blank"
              >
                github.com/bird-sanctuary/bluejay
              </a>

              <br />

              AM32 GitHub：
              {' '}

              <a
                href="https://github.com/am32-firmware/AM32"
                rel="noopener noreferrer"
                target="_blank"
              >
                github.com/am32-firmware/AM32
              </a>
            </p>
          </div>

          <div className="welcome-section">
            <h3>
              📋 免责声明
            </h3>

            <p>
              本站与 ESC Configurator 官方团队无任何关联，所有软件版权归原项目所有。本站仅提供下载加速服务，不对软件内容进行任何修改。
            </p>
          </div>

          <div className="welcome-section">
            <h3>
              📧 联系方式
            </h3>

            <p>
              官方网站：
              {' '}

              <a
                href="https://fpv.yulovehan.top"
                rel="noopener noreferrer"
                target="_blank"
              >
                fpv.yulovehan.top
              </a>

              <br />

              邮箱：
              {' '}

              <a href="mailto:13142326159@139.com">
                13142326159@139.com
              </a>

              <br />

              电话：13142326159

              <span className="phone-notice">
                （除非必要，否则不要打电话）
              </span>

              <br />

              抖音号：
              {' '}

              <a
                href="https://www.douyin.com/search/56017131097"
                rel="noopener noreferrer"
                target="_blank"
              >
                56017131097
              </a>

              <br />

              哔哩哔哩：
              {' '}

              <a
                href="https://space.bilibili.com/3546789256890878"
                rel="noopener noreferrer"
                target="_blank"
              >
                @流星雨来勒
              </a>

              <br />

              QQ 群：
              {' '}

              <a
                href="https://qm.qq.com/q/rkh8s07lRe"
                rel="noopener noreferrer"
                target="_blank"
              >
                点击加入【流星雨 betaflight 加速】
              </a>

              <span>
                （群号：613515032）
              </span>
            </p>
          </div>

          <div className="priority-notice">
            <h4>
              ⚠️ 联系提示
            </h4>

            <p>
              <strong>
                有问题优先加入 QQ 群，发送问题截图并艾特群主
              </strong>

              <br />

              因为本人作息非常不规律，所以白天可能都不在线
            </p>
          </div>
        </div>

        <div className="agree-checkbox">
          <label>
            <input
              checked={agreed}
              onChange={handleCheckboxChange}
              type="checkbox"
            />

            <span>
              我已阅读并同意上述内容
            </span>
          </label>
        </div>

        <div className="welcome-actions">
          <button
            className="confirm-btn"
            disabled={!agreed}
            onClick={handleConfirm}
            type="button"
          >
            开始使用
          </button>
        </div>
      </div>
    </div>
  );
}

WelcomeDialog.propTypes = { onConfirm: PropTypes.func.isRequired };

function HomeColumnLeft() {
  const { t } = useTranslation('common');

  const disclaimerLines = [1, 2, 3, 4, 5, 6].map((index) => {
    const line = `homeDisclaimerTextLine${index}`;

    return (
      <ReactMarkdown key={line}>
        {t(line)}
      </ReactMarkdown>
    );
  });

  return(
    <div className="column third_left text1">
      <div className="wrap">
        <div className="summary-section">
          <h2>
            {t('homeDisclaimerHeader')}
          </h2>

          <div>
            {disclaimerLines}
          </div>
        </div>

        <div className="summary-section">
          <h2>
            {t('homeAttributionHeader')}
          </h2>

          <div>
            <ReactMarkdown>
              {t('homeAttributionText')}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeColumnCenter() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();

  const handleOpenMelodyEditor = useCallback(() => {
    dispatch(melodyEditorDummy());
    dispatch(showMelodyEditor());
  }, [dispatch]);

  return(
    <div className="column third_center text2 tab">
      <div className="wrap">
        <div className="alert">
          <strong>
            Attention Bluejay users!
          </strong>

          <br />
          If you are still on 0.20.0, please upgrade to version 0.21.0 - there have been issues with stall detection and motor protection which might result in broken ESCs and/or motors.
        </div>

        <h2>
          {t('homeExperimental')}
        </h2>

        <div>
          {t('homeVersionInfo')}

          <ul>
            <li>
              <a
                href="https://github.com/bitdump/BLHeli"
                rel="noreferrer"
                target="_blank"
              >
                BLHeli_S
              </a>
            </li>

            <li>
              <a
                href="https://github.com/bird-sanctuary/bluejay"
                rel="noreferrer"
                target="_blank"
              >
                Bluejay
              </a>
            </li>

            <li>
              <a
                href="https://github.com/am32-firmware/AM32"
                rel="noreferrer"
                target="_blank"
              >
                AM32
              </a>
            </li>
          </ul>
        </div>

        <div className="summary-section">
          <h3>
            BLHeli_S
          </h3>

          <section>
            <div>
              <ReactMarkdown>
                {t('blhelisTextLine1')}
              </ReactMarkdown>

              <ReactMarkdown>
                {t('blhelisTextLine2')}
              </ReactMarkdown>
            </div>
          </section>
        </div>

        <div className="summary-section">
          <h3>
            Bluejay
          </h3>

          <section>
            <img
              alt="Bluejay"
              src={bluejay}
            />

            <div>
              <div>
                <ReactMarkdown>
                  {t('bluejayTextLine1')}
                </ReactMarkdown>

                <ReactMarkdown>
                  {t('bluejayTextLine2')}
                </ReactMarkdown>
              </div>

              <div className="default-btn melody-editor-button">
                <button
                  onClick={handleOpenMelodyEditor}
                  type="button"
                >
                  {t('openMelodyEditor')}
                </button>
              </div>

              <div>
                <ReactMarkdown>
                  {t('bluejaySupportedHardwareLine1')}
                </ReactMarkdown>

                <ReactMarkdown>
                  {t('bluejaySupportedHardwareLine2')}
                </ReactMarkdown>
              </div>
            </div>
          </section>
        </div>

        <div className="summary-section">
          <h3>
            AM32
          </h3>

          <section>
            <div>
              <ReactMarkdown>
                {t('blheli32ToAM32Line1')}
              </ReactMarkdown>

              <ReactMarkdown>
                {t('blheli32ToAM32Line2')}
              </ReactMarkdown>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function HomeColumnRight() {
  const { t } = useTranslation('common');
  const contributionItems = [1, 2, 3, 4, 5].map((index) => {
    const line = `homeContributionItem${index}`;

    return(
      <ReactMarkdown
        components={{ p: 'li' }}
        key={line}
      >
        {t(line)}
      </ReactMarkdown>
    );
  });

  return(
    <div className="column third_right text3">
      <div className="wrap">
        <div className="summary-section">
          <h2>
            {t('homeDiscordHeader')}
          </h2>

          <div>
            {t('homeDiscordText')}
          </div>

          <a
            className="discord-link"
            href="https://discord.gg/QvSS5dk23C"
            rel="noreferrer"
            target="_blank"
          >
            <img
              alt="Discord"
              className="discord"
              data-canonical-src="https://img.shields.io/discord/822952715944460368.svg?label=&amp;logo=discord&amp;logoColor=ffffff&amp;color=7389D8&amp;labelColor=6A7EC2"
              src="https://camo.githubusercontent.com/74d2e4746c6f20a2cf36068cd18d092724801f7ccd17e6bdce62e94d31f9ccb2/68747470733a2f2f696d672e736869656c64732e696f2f646973636f72642f3832323935323731353934343436303336382e7376673f6c6162656c3d266c6f676f3d646973636f7264266c6f676f436f6c6f723d66666666666626636f6c6f723d373338394438266c6162656c436f6c6f723d364137454332"
            />
          </a>
        </div>

        <div className="summary-section">
          <h2>
            {t('homeChinaHeader')}
          </h2>

          <ReactMarkdown components={{ p: 'div' }}>
            {t('homeChinaText')}
          </ReactMarkdown>
        </div>

        <div className="summary-section">
          <h2>
            {t('homeContributionHeader')}
          </h2>

          <div>
            <ReactMarkdown components={{ p: 'div' }}>
              {t('homeContributionText')}
            </ReactMarkdown>

            <ul>
              {contributionItems}
            </ul>
          </div>
        </div>

        <div className="summary-section">
          <h3>
            {t('whatsNextHeader')}
          </h3>

          <section>
            <ReactMarkdown components={{ p: 'div' }}>
              {t('whatsNextText')}
            </ReactMarkdown>
          </section>
        </div>
      </div>
    </div>
  );
}

function Home() {
  const { t } = useTranslation('common');
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('esc_configurator_welcome_shown');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
  }, []);

  const handleWelcomeClose = useCallback(() => {
    setShowWelcome(false);
  }, []);

  const markdownComponents = { p: 'span' };
  const markdownDivComponents = { p: 'div' };

  const renderWelcomeDialog = useCallback(() => {
    if (showWelcome) {
      return <WelcomeDialog onConfirm={handleWelcomeClose} />;
    }
    return null;
  }, [showWelcome, handleWelcomeClose]);

  return (
    <div id="content">
      {renderWelcomeDialog()}

      <div id="tab-landing">
        <div className="content_wrapper">
          <div className="content_top">
            <div
              align="center"
              className="logowrapper"
            >
              <div
                align="center"
                className="line-1"
              >
                <ReactMarkdown components={markdownComponents}>
                  {t('homeWelcome')}
                </ReactMarkdown>
              </div>

              <Install />

              <div>
                <div align="center">
                  <ReactMarkdown components={markdownDivComponents}>
                    {t('betaWarningLine1')}
                  </ReactMarkdown>

                  <br />

                  <ReactMarkdown components={markdownDivComponents}>
                    {t('betaWarningLine2')}
                  </ReactMarkdown>
                </div>

                <div align="center">
                  <ReactMarkdown components={{ p: 'div' }}>
                    {t('findHelp')}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>

          <div className="content_mid">
            <HomeColumnLeft />

            <HomeColumnCenter />

            <HomeColumnRight />
          </div>

          <div
            className="mirror-about"
            id="mirrorAbout"
          >
            <h2 className="mirror-title">
              🌠 关于流星雨加速站
            </h2>

            <div className="mirror-content">
              <div className="mirror-info">
                <p className="mirror-desc">
                  流星雨加速站是一个为国内穿越机爱好者提供的 ESC Configurator 镜像服务，旨在解决国内访问 GitHub 速度慢、不稳定的问题。
                </p>

                <div className="mirror-section">
                  <h3>
                    🚀 加速服务
                  </h3>

                  <ul>
                    <li>
                      <strong>
                        固件下载加速
                      </strong>

                      {' '}
                      - 快速下载 BLHeli、Bluejay、AM32 固件
                    </li>

                    <li>
                      <strong>
                        API 代理加速
                      </strong>

                      {' '}
                      - 固件列表、版本信息等 API 加速
                    </li>

                    <li>
                      <strong>
                        完整中文化
                      </strong>

                      {' '}
                      - 所有界面均已翻译为中文
                    </li>
                  </ul>
                </div>

                <div className="mirror-section">
                  <h3>
                    📋 版本信息
                  </h3>

                  <table className="mirror-table">
                    <tbody>
                      <tr>
                        <td>
                          基于
                        </td>

                        <td>
                          ESC Configurator v0.32.1 官方版本
                        </td>
                      </tr>

                      <tr>
                        <td>
                          修改内容
                        </td>

                        <td>
                          默认中文 + 服务器加速 + 流星雨标识
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mirror-section">
                  <h3>
                    🌐 相关链接
                  </h3>

                  <ul>
                    <li>
                      <a
                        href="https://github.com/bitdump/BLHeli"
                        target="_blank"
                      >
                        BLHeli GitHub
                      </a>
                    </li>

                    <li>
                      <a
                        href="https://github.com/bird-sanctuary/bluejay"
                        target="_blank"
                      >
                        Bluejay GitHub
                      </a>
                    </li>

                    <li>
                      <a
                        href="https://github.com/am32-firmware/AM32"
                        target="_blank"
                      >
                        AM32 GitHub
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="mirror-section mirror-disclaimer">
                  <h3>
                    ⚠️ 免责声明
                  </h3>

                  <p>
                    本镜像站仅提供网络加速服务，不修改 ESC Configurator 的核心功能。所有软件版权归原项目所有。
                  </p>

                  <p>
                    使用本服务即表示您同意遵守相关法律法规，不得利用本服务进行任何违法活动。
                  </p>
                </div>

                <div className="mirror-section">
                  <h3>
                    📧 联系方式
                  </h3>

                  <ul>
                    <li>
                      官方网站：
                      {' '}

                      <a
                        href="https://fpv.yulovehan.top"
                        target="_blank"
                      >
                        fpv.yulovehan.top
                      </a>
                    </li>

                    <li>
                      抖音号：
                      {' '}

                      <a
                        href="https://www.douyin.com/search/56017131097"
                        target="_blank"
                      >
                        56017131097
                      </a>
                    </li>

                    <li>
                      哔哩哔哩：
                      {' '}

                      <a
                        href="https://space.bilibili.com/3546789256890878"
                        target="_blank"
                      >
                        @流星雨来勒
                      </a>
                    </li>

                    <li>
                      QQ 群：
                      {' '}

                      <a
                        href="https://qm.qq.com/q/rkh8s07lRe"
                        target="_blank"
                      >
                        流星雨加速交流群 (613515032)
                      </a>
                    </li>

                    <li>
                      邮箱：
                      {' '}

                      <a href="mailto:13142326159@139.com">
                        13142326159@139.com
                      </a>
                    </li>

                    <li>
                      电话：13142326159
                    </li>
                  </ul>
                </div>
              </div>

              <div className="sponsor-section">
                <h3 className="sponsor-title">
                  ❤️ 赞助支持
                </h3>

                <p className="sponsor-desc">
                  如果觉得好用，欢迎赞助支持！
                </p>

                <div className="sponsor-codes">
                  <div className="sponsor-item">
                    <img
                      alt="微信赞助码"
                      src="https://zyz.yulovehan.top/d/1/%E6%88%91%E7%9A%84%E5%9B%BE%E7%89%87/wx.png"
                    />

                    <span>
                      微信
                    </span>
                  </div>

                  <div className="sponsor-item">
                    <img
                      alt="支付宝赞助码"
                      src="https://zyz.yulovehan.top/d/1/%E6%88%91%E7%9A%84%E5%9B%BE%E7%89%87/zfb.png"
                    />

                    <span>
                      支付宝
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default Home;
